import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../styles/Home.css'; 

function Home() {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      const response = await fetch(`${baseURL}/homepage-sections.php`);
      if (response.ok) {
        const data = await response.json();
        console.log('API Data:', data); // Debug log
        setHomeData(data);
      } else {
        throw new Error('Failed to fetch homepage data');
      }
    } catch (err) {
      setError('Error loading homepage content. Please try again later.');
      console.error('Error fetching homepage data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSection = (sectionName) => {
    if (!homeData?.sections) return null;
    return homeData.sections.find(section => section.section_name === sectionName);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary" className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 text-center">
        <div className="alert alert-danger">{error}</div>
        <Button onClick={fetchHomeData} variant="primary">
          Try Again
        </Button>
      </Container>
    );
  }

  const heroSection = getSection('hero');
  const servicesSection = getSection('services_preview');
  const ctaSection = getSection('cta');

  return (
    <>
      {/* Modern Hero Section with Background Image */}
      <section className="modern-hero">
        <div className="hero-background">
          <img 
            src="/images/hero/herobg.jpeg" 
            alt="Laboratory Background" 
            className="background-image"
            onError={(e) => {
              console.error('Hero background image failed to load');
              e.target.style.display = 'none';
            }}
          />
          <div className="hero-overlay"></div>
        </div>
        <Container>
          <Row className="align-items-center min-vh-100">
            <Col lg={6} className="hero-content">
              <h1 className="hero-title">
                {heroSection?.section_title || 'Precision That Powers Solutions'}
              </h1>
              <p className="hero-subtitle">
                {heroSection?.section_content || 'Trusted provider of high-quality laboratory equipment and analytical solutions across East Africa and beyond.'}
              </p>
              <div className="hero-buttons">
                <Button as={Link} to={heroSection?.button_link || '/booking'} className="btn-primary-custom">
                  {heroSection?.button_text || 'Request a Quote'}
                </Button>
                <Button as={Link} to="/services" className="btn-outline-custom">
                  Our Services
                </Button>
              </div>
            </Col>
            <Col lg={6} className="hero-visual">
              <div className="floating-card">
                <img 
                  src="/images/hero/lab-equipment.jpeg" 
                  alt="Advanced Laboratory Equipment"
                  className="hero-equipment-img"
                  onError={(e) => {
                    console.error('Hero equipment image failed to load');
                    e.target.style.display = 'none';
                  }}
                />
                <div className="floating-badge">
                  <i className="fas fa-award"></i>
                  <span>Trusted Since 2020</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section with Images - FIXED */}
      <section className="services-section py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="section-title">
                {servicesSection?.section_title || 'Our Services'}
              </h2>
              <p className="section-subtitle">
                {servicesSection?.section_content || 'Comprehensive laboratory solutions tailored to your needs'}
              </p>
            </Col>
          </Row>
          <Row>
            {homeData?.services && homeData.services.length > 0 ? (
              homeData.services.map((service) => {
                // Use the actual image path from the API, not generated paths
                const imagePath = service.image || `/images/services/${service.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}.jpg`;
                
                return (
                  <Col lg={4} md={6} className="mb-4" key={service.id}>
                    <Card className="modern-service-card">
                      <div className="card-image-container">
                        <img 
                          src={imagePath} 
                          alt={service.title}
                          className="card-image"
                          onError={(e) => {
                            console.error(`Service image failed: ${imagePath}`);
                            // Fallback to placeholder
                            e.target.src = '/images/services/default-service.jpeg';
                          }}
                          onLoad={() => console.log(`Service image loaded: ${imagePath}`)}
                        />
                        <div className="card-overlay"></div>
                        <div className="service-icon">
                          <i className={service.icon || `fas fa-cog`}></i>
                        </div>
                      </div>
                      <Card.Body>
                        <Card.Title>{service.title}</Card.Title>
                        <Card.Text>{service.description}</Card.Text>
                        <Button as={Link} to={service.link || '/services'} className="btn-service">
                          Learn More <i className="fas fa-arrow-right"></i>
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })
            ) : (
              // Fallback services with CORRECT image paths
              [
                {
                  id: 1,
                  title: "Analytical Instruments",
                  description: "HPLC, GC, Spectroscopy systems and more from leading global manufacturers.",
                  icon: "fas fa-microscope",
                  image: "/images/services/analytical-instruments.jpg" // Fixed path
                },
                {
                  id: 2,
                  title: "Maintenance & Repairs",
                  description: "Cost-effective service programs to ensure reliability and minimize downtime.",
                  icon: "fas fa-tools",
                  image: "/images/services/maintenance-repairs.jpg" // Fixed filename
                },
                {
                  id: 3,
                  title: "Training & Capacity Building",
                  description: "Hands-on training for efficient equipment use and technical challenges.",
                  icon: "fas fa-chalkboard-teacher",
                  image: "/images/services/training-capacity.jpg" // Fixed path
                }
              ].map((service) => (
                <Col lg={4} md={6} className="mb-4" key={service.id}>
                  <Card className="modern-service-card">
                    <div className="card-image-container">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="card-image"
                        onError={(e) => {
                          console.error(`Fallback image failed: ${service.image}`);
                          e.target.src = '/images/services/default-service.jpeg';
                        }}
                      />
                      <div className="card-overlay"></div>
                      <div className="service-icon">
                        <i className={service.icon}></i>
                      </div>
                    </div>
                    <Card.Body>
                      <Card.Title>{service.title}</Card.Title>
                      <Card.Text>{service.description}</Card.Text>
                      <Button as={Link} to="/services" className="btn-service">
                        Learn More <i className="fas fa-arrow-right"></i>
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Container>
      </section>

      {/* Client Logos Section with Error Handling */}
      <section className="clients-section py-4 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col className="text-center">
              <p className="mb-3 text-muted">Trusted by leading organizations</p>
              <div className="client-logos">
                {[
                  { src: "/images/clients/tea-board-logo.png", alt: "Tea Board of Kenya" },
                  { src: "/images/clients/kirdi-logo.jpeg", alt: "KIRDI" },
                  { src: "/images/clients/brookside-logo.png", alt: "Brookside Dairies" },
                  { src: "/images/clients/usiu-logo.png", alt: "USIU" },
                  { src: "/images/clients/uon-logo.jpg", alt: "University of Nairobi" }
                ].map((client, index) => (
                  <img 
                    key={index}
                    src={client.src} 
                    alt={client.alt}
                    onError={(e) => {
                      console.error(`Client logo failed: ${client.src}`);
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials with Images */}
        {/* Testimonials with Backend Images */}
        {homeData?.testimonials && homeData.testimonials.length > 0 && (
        <section className="testimonials-section py-5">
            <Container>
            <Row className="text-center mb-5">
                <Col>
                <h2 className="section-title">What Our Clients Say</h2>
                <p className="section-subtitle">
                    Trusted by leading institutions and industries across East Africa
                </p>
                </Col>
            </Row>
            <Row>
                <Col>
                <Carousel indicators={false} controls={homeData.testimonials.length > 1}>
                    {homeData.testimonials.map((testimonial) => (
                    <Carousel.Item key={testimonial.id}>
                        <div className="testimonial-card">
                        <div className="testimonial-content">
                            <div className="testimonial-image">
                            <img 
                                src={testimonial.client_image || `/images/testimonials/default-avatar.jpg`} 
                                alt={testimonial.client_name}
                                onError={(e) => {
                                console.error(`Testimonial image failed: ${testimonial.client_image}`);
                                // Fallback options:
                                
                                // Option 1: Use initials as fallback
                                if (!testimonial.client_image) {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `
                                    <div class="initials-avatar">
                                        ${getInitials(testimonial.client_name)}
                                    </div>
                                    `;
                                } else {
                                    // Option 2: Use default avatar
                                    e.target.src = '/images/testimonials/default-avatar.jpg';
                                }
                                }}
                                onLoad={() => console.log(`✅ Testimonial image loaded: ${testimonial.client_image}`)}
                            />
                            </div>
                            <div className="testimonial-text">
                            <div className="stars mb-3">
                                {[...Array(5)].map((_, i) => (
                                <i 
                                    key={i} 
                                    className={`fas fa-star ${i < testimonial.rating ? 'text-warning' : 'text-muted'}`}
                                ></i>
                                ))}
                            </div>
                            <p className="testimonial-quote">"{testimonial.testimonial_text}"</p>
                            <footer className="testimonial-author">
                                <strong>{testimonial.client_name}</strong>
                                {testimonial.client_position && `, ${testimonial.client_position}`}
                                {testimonial.client_company && (
                                <span className="company"> at {testimonial.client_company}</span>
                                )}
                            </footer>
                            </div>
                        </div>
                        </div>
                    </Carousel.Item>
                    ))}
                </Carousel>
                </Col>
            </Row>
            </Container>
        </section>
        )}

      {/* Rest of your components remain the same */}
      <section className="cta-section">
        {/* ... existing cta section ... */}
      </section>

      <section className="stats-section py-5">
        {/* ... existing stats section ... */}
      </section>
    </>
  );
}

export default Home;